import React, { useEffect, useState } from "react";
import { Drawer, DrawerRef } from "../../../components/drawers/Drawer";
import Modal from "../../../components/base/Modal";
import Indicator from "../../../components/base/Indicator";
import Icon from "../../../components/base/Icon";
import { useHub } from "../../../core/Global";
import { toastError, toastSuccess } from "../../../components/base/Toast";
import { Quote, QuoteDetail, QuoteStatus, smsText, statusClass, statusText } from "../models/Enterprise";
import { toCount, toDate, toDateTime, toMoney, toRate, toUnit } from "../../plancatalog/components/format";


const eventText: Record<string, string> = {
    created: "Created", edited: "Edited", approved: "Approved", sent: "Sent", opened: "Opened by the customer",
    change: "Change requested", details: "Company details given", signed: "Signed", account: "Account sign-up started",
    payment: "Payment started", paid: "Paid", activated: "Activated", revoked: "Withdrawn", expired: "Expired",
    version: "New version", sms: "Messaging registration"
}

const eventIcon: Record<string, string> = {
    created: "plus", edited: "pen", approved: "user-shield", sent: "paper-plane", opened: "eye", change: "message-pen",
    details: "building", signed: "file-signature", account: "user-plus", payment: "credit-card", paid: "credit-card",
    activated: "circle-check", revoked: "ban", expired: "clock", version: "code-branch", sms: "comment-sms"
}


// One offer, everything about it: what it says, the internal figures, where the customer is
// on the link, what they asked to change, and what happened when.
export default function QuoteDetailDrawer(props: {
    id: string,
    onClose: () => void,
    onChanged: () => void,
    onEdit: (q: Quote) => void,
    onOpenVersion: (id: string) => void
}) {

    const hub = useHub()
    const drawer = React.useRef<DrawerRef>(null)

    const [detail, detailSet] = useState<QuoteDetail>()
    const [working, workingSet] = useState<string>()
    const [revokeReason, revokeReasonSet] = useState<string>("")
    const [approvalNote, approvalNoteSet] = useState<string>("")
    const [sms, smsSet] = useState<{ status: string, note: string }>({ status: "not_started", note: "" })

    const load = () => {
        hub.Get("/system/enterprise/" + props.id, res => {
            if (res.success) {
                detailSet(res.data)
                smsSet({ status: res.data.quote.smsRegistrationStatus, note: res.data.quote.smsRegistrationNote ?? "" })
            }
            else toastError(res.message ?? "Couldn't load the offer.")
        })
    }

    useEffect(() => {
        load()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.id])

    const act = (path: string, body: any, done: string, after?: (q: Quote) => void, callback?: (keepOpen: boolean) => void) => {
        workingSet(path)
        hub.Post(`/system/enterprise/${props.id}/${path}`, body ?? {}, res => {
            workingSet(undefined)
            callback?.(!res.success)
            if (!res.success) return toastError(res.message ?? "Couldn't do that.")
            toastSuccess(done)
            props.onChanged()
            if (after) after(res.data)
            else load()
        }, () => { workingSet(undefined); callback?.(true) })
    }

    const q = detail?.quote
    const status = q?.status as QuoteStatus
    const outWithCustomer = status === "sent" || status === "accepted"
    const needsApproval = !!q && q.requiredApproval !== "none" && rank(q.approvedLevel) < rank(q.requiredApproval)

    const copy = () => {
        if (!detail?.link) return
        navigator.clipboard?.writeText(detail.link)
        toastSuccess("Link copied.")
    }

    return (
        <Drawer ref={drawer} id="drawer-enterprise-detail"
            title={q ? `Q-${q.number}${q.version > 1 ? " v" + q.version : ""} · ${q.companyName ?? ""}` : "Offer"}
            titleIcon="file-signature"
            subtitle={q ? `${statusText[status]} · ${toMoney(q.monthly)} a month` : undefined}
            size="large"
            onClose={props.onClose}>

            {
                !q ?
                    <div className="py-20"><Indicator /></div>
                    :
                    <div className="ent-detail">

                        {/* ------------------------------------------------ what to do next */}
                        <div className="ent-actions">
                            <span className={"badge " + statusClass[status]}>{statusText[status]}</span>

                            {
                                status === "draft" && <>
                                    <button type="button" className="btn btn-sm btn-light-primary" onClick={() => { props.onEdit(q) }}>
                                        <Icon icon="pen" size={11} className="me-2" />Edit
                                    </button>
                                    {
                                        needsApproval &&
                                        <button type="button" className="btn btn-sm btn-light-warning" disabled={q.blockers.length > 0}
                                            onClick={() => window.openModal("modal-enterprise-approve")}>
                                            <Icon icon="user-shield" size={11} className="me-2" />Approve
                                        </button>
                                    }
                                    <button type="button" className="btn btn-sm btn-primary"
                                        disabled={q.blockers.length > 0 || needsApproval || !!working}
                                        onClick={() => window.openModal("modal-enterprise-send")}>
                                        <Icon icon="paper-plane" size={11} className="me-2" />Send to {q.contactEmail}
                                    </button>
                                    {
                                        q.version === 1 && !q.sentAt &&
                                        <button type="button" className="btn btn-sm btn-light-danger ms-auto" onClick={() => window.openModal("modal-enterprise-delete")}>
                                            <Icon icon="trash" size={11} className="me-2" />Delete draft
                                        </button>
                                    }
                                </>
                            }

                            {
                                outWithCustomer && <>
                                    <button type="button" className="btn btn-sm btn-light-primary" onClick={copy}>
                                        <Icon icon="link" size={11} className="me-2" />Copy link
                                    </button>
                                    <button type="button" className="btn btn-sm btn-light" disabled={!!working}
                                        onClick={() => window.openModal("modal-enterprise-version")}>
                                        <Icon icon="code-branch" size={11} className="me-2" />New version
                                    </button>
                                    <button type="button" className="btn btn-sm btn-light-danger ms-auto" onClick={() => window.openModal("modal-enterprise-revoke")}>
                                        <Icon icon="ban" size={11} className="me-2" />Withdraw
                                    </button>
                                </>
                            }

                            {
                                (status === "expired" || status === "revoked") &&
                                <button type="button" className="btn btn-sm btn-light-primary" disabled={!!working}
                                    onClick={() => act("version", {}, "New version created as a draft.", nq => props.onOpenVersion(nq.id!))}>
                                    <Icon icon="code-branch" size={11} className="me-2" />Make a new version
                                </button>
                            }
                        </div>

                        {
                            status === "draft" && q.blockers.length > 0 &&
                            <div className="ent-callout is-bad">
                                <Icon icon="ban" type="solid" size={13} />
                                <div>
                                    <b>Cannot be sent yet</b>
                                    <ul className="mb-0 mt-1">{q.blockers.map((b, i) => <li key={i}>{b}</li>)}</ul>
                                </div>
                            </div>
                        }

                        {
                            status === "draft" && q.blockers.length === 0 && needsApproval &&
                            <div className="ent-callout is-warn">
                                <Icon icon="user-shield" type="solid" size={13} />
                                <div>
                                    Needs {q.requiredApproval === "director" ? "a director's" : "a sales manager's"} approval before it can go out —
                                    {" "}{q.discountPercent}% discount, {q.marginPercent}% margin.
                                </div>
                            </div>
                        }

                        {
                            q.approvedAt &&
                            <div className="ent-callout is-good">
                                <Icon icon="user-shield" type="solid" size={13} />
                                <div>Approved as {q.approvedLevel} by {q.approvedByName}, {toDateTime(q.approvedAt)}{q.approvalNote && <> — “{q.approvalNote}”</>}</div>
                            </div>
                        }

                        {
                            (status === "revoked" || status === "superseded") &&
                            <div className="ent-callout">
                                <Icon icon="circle-info" type="solid" size={13} />
                                <div>{q.revokedReason} The link no longer works.</div>
                            </div>
                        }

                        {
                            outWithCustomer && detail?.link &&
                            <div className="ent-link">
                                <Icon icon="link" size={11} />
                                <span className="text-truncate">{detail.link}</span>
                                <span className="text-gray-500 fs-8 ms-auto flex-shrink-0">open until {toDate(q.validUntil)}</span>
                            </div>
                        }

                        {/* ------------------------------------------------ change requests */}
                        {
                            q.changeRequests.length > 0 &&
                            <div className="ent-section">
                                <div className="ent-section-title">Change requests from the customer</div>
                                {
                                    q.changeRequests.map((c, i) =>
                                        <div key={i} className="ent-change">
                                            <div className="text-gray-500 fs-8">{c.name} · {toDateTime(c.at)}</div>
                                            <div className="text-gray-900">{c.text}</div>
                                        </div>
                                    )
                                }
                                {outWithCustomer && <div className="ent-note">To change the offer, make a new version: the customer gets a fresh link and the old one stops working.</div>}
                            </div>
                        }

                        {/* ------------------------------------------------ the customer's progress */}
                        {
                            status !== "draft" &&
                            <div className="ent-section">
                                <div className="ent-section-title">Onboarding</div>
                                <div className="ent-steps">
                                    <Step done={q.openCount > 0} title="Opened"
                                        text={q.openCount > 0 ? `${q.openCount} time${q.openCount === 1 ? "" : "s"}, last ${toDateTime(q.lastOpenedAt)}` : "Not yet"} />
                                    <Step done={!!q.billingDetails} title="Company & billing"
                                        text={q.billingDetails ? `${q.billingDetails.legalName}${q.billingDetails.taxId ? " · tax ID " + q.billingDetails.taxId : ""}${q.billingDetails.poNumber ? " · PO " + q.billingDetails.poNumber : ""} · invoices to ${q.billingDetails.billingContactName} <${q.billingDetails.billingContactEmail}>` : "Not yet"} />
                                    <Step done={!!q.signature} title="Signed"
                                        text={q.signature ? `${q.signature.name}, ${q.signature.title} · ${toDateTime(q.signature.signedAt)} · from ${q.signature.ip}` : "Not yet"}>
                                        {
                                            q.signature &&
                                            <div className="ent-fingerprints">
                                                {q.signature.documents.map(d => <div key={d.key}>{d.title} <code>{d.fingerprint?.substring(0, 16)}…</code></div>)}
                                            </div>
                                        }
                                    </Step>
                                    <Step done={!!q.accountUserId} title={q.existingUserId ? "Existing account" : "Account"}
                                        text={q.existingUserId ? (detail?.existingAccountName ?? "—") : q.accountUserId ? "Created" : "Not yet"} />
                                    <Step done={status === "active"} title="Paid & active"
                                        text={status === "active" ? `Since ${toDateTime(q.activatedAt)}` : "Not yet"} />
                                    <Step done={q.smsRegistrationStatus === "approved"} warn={q.smsRegistrationStatus === "rejected"} title="Messaging registration (10DLC)"
                                        text={smsText[q.smsRegistrationStatus] + (q.smsRegistrationNote ? " — " + q.smsRegistrationNote : "")}>
                                        <div className="d-flex flex-wrap gap-2 mt-2">
                                            <select className="form-select form-select-sm form-select-solid w-auto" value={sms.status}
                                                onChange={e => smsSet({ ...sms, status: e.target.value })}>
                                                {Object.entries(smsText).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                                            </select>
                                            <input className="form-control form-control-sm form-control-solid flex-grow-1" style={{ minWidth: 160 }}
                                                placeholder="Note (campaign ID, reason…)" value={sms.note} onChange={e => smsSet({ ...sms, note: e.target.value })} />
                                            <button type="button" className="btn btn-sm btn-light-primary" disabled={!!working}
                                                onClick={() => act("sms", sms, "Registration status saved.")}>Save</button>
                                        </div>
                                    </Step>
                                </div>
                            </div>
                        }

                        {/* ------------------------------------------------ the offer */}
                        <div className="ent-section">
                            <div className="ent-section-title">The offer</div>
                            <div className="ent-detail-lines">
                                {
                                    q.lines.map((l, i) =>
                                        <div key={i} className="ent-detail-line">
                                            <span className="text-gray-900 fw-semibold">{l.label}</span>
                                            <span className="text-gray-600">{toCount(l.quantity)} × {toUnit(l.unitPrice)}{l.overridden && <em> (list {toUnit(l.listUnitPrice)})</em>}</span>
                                            <span className="fw-bold text-end">{toMoney(l.total)}{l.oneTime ? " once" : "/mo"}</span>
                                        </div>
                                    )
                                }
                            </div>
                            <div className="ent-detail-grid mt-4">
                                <Fact label="Monthly" value={toMoney(q.monthly)} />
                                <Fact label="One-time" value={toMoney(q.oneTime)} />
                                <Fact label="Billed" value={q.billing === "annual" ? `Yearly, ${toMoney(q.periodPrice)}` : "Monthly"} />
                                <Fact label="Charged on accepting" value={toMoney(q.firstCharge)} />
                                <Fact label="Term" value={`${q.termMonths} months${q.autoRenew ? `, renews +${q.renewalUpliftPercent}%` : ", no auto-renew"}`} />
                                <Fact label="Per message / minute beyond" value={`${toRate(q.overageMessageRate)} / ${toRate(q.overageMinuteRate)}`} />
                                <Fact label="Extra number / user" value={`${toMoney(q.extraNumberPrice)} / ${toMoney(q.extraUserPrice)}`} />
                                <Fact label="Included" value={[q.whatsAppIncluded && "WhatsApp", q.telegramIncluded && "Telegram", q.extensionsSupportIncluded && "Masked Job Calls", q.callRecordingIncluded && `Recording (${q.recordingRetentionMonths} mo)`].filter(Boolean).join(", ") || "—"} />
                                <Fact label="Support" value={q.supportTier || "—"} />
                                <Fact label="Contact" value={`${q.contactName} · ${q.contactEmail}${q.contactPhone ? " · " + q.contactPhone : ""}`} />
                            </div>
                            {q.customerNote && <div className="ent-note"><b>Note to the customer:</b> {q.customerNote}</div>}
                        </div>

                        <div className="ent-section">
                            <div className="ent-section-title"><Icon icon="lock" size={10} className="me-2" />Internal</div>
                            <div className="ent-detail-grid">
                                <Fact label="Discount" value={`${q.discountPercent}%`} />
                                <Fact label="Estimated cost / month" value={toMoney(q.estimatedMonthlyCost)} />
                                <Fact label="Gross margin" value={`${q.marginPercent}%`} />
                                <Fact label="Per message / per user" value={`${toRate(q.effectivePerMessage)} / ${toMoney(q.effectivePerUser)}`} />
                                <Fact label="Approval needed" value={q.requiredApproval} />
                                <Fact label="Built by" value={`${q.createdByName ?? "—"}, ${toDate(q.createdAt)}`} />
                            </div>
                            {q.internalNote && <div className="ent-note">{q.internalNote}</div>}
                        </div>

                        {/* ------------------------------------------------ versions and history */}
                        {
                            (detail?.versions.length ?? 0) > 1 &&
                            <div className="ent-section">
                                <div className="ent-section-title">Versions</div>
                                <div className="d-flex flex-wrap gap-2">
                                    {
                                        detail!.versions.map(v =>
                                            <button key={v.id} type="button" className={"ent-filter" + (v.id === q.id ? " is-active" : "")}
                                                onClick={() => v.id !== q.id && props.onOpenVersion(v.id!)}>
                                                v{v.version} · {statusText[v.status as QuoteStatus]}
                                                <span>{toMoney(v.monthly)}</span>
                                            </button>
                                        )
                                    }
                                </div>
                            </div>
                        }

                        <div className="ent-section">
                            <div className="ent-section-title">History</div>
                            <div className="ent-history">
                                {
                                    detail!.events.map(e =>
                                        <div key={e.id} className={"ent-event is-" + e.kind}>
                                            <div className="ent-event-icon"><Icon icon={(eventIcon[e.kind ?? ""] ?? "circle") as any} size={10} /></div>
                                            <div className="min-w-0">
                                                <div className="text-gray-900 fw-semibold fs-7">
                                                    {eventText[e.kind ?? ""] ?? e.kind}
                                                    {detail!.versions.length > 1 && <span className="text-gray-500 fw-normal"> · v{e.version}</span>}
                                                </div>
                                                <div className="text-gray-500 fs-8">
                                                    {toDateTime(e.at)}{e.byName && <> · {e.byName}</>}{e.ip && <> · {e.ip}</>}
                                                </div>
                                                {e.note && <div className="text-gray-700 fs-8 mt-1">{e.note}</div>}
                                            </div>
                                        </div>
                                    )
                                }
                            </div>
                        </div>

                    </div>
            }

            <Modal id="modal-enterprise-send" title="Send this offer?" buttonText="Send" buttonColor="primary"
                onConfirm={cb => act("send", {}, `Sent to ${q?.contactEmail}.`, undefined, cb)}>
                <p className="mb-0">
                    {q?.contactName} at {q?.contactEmail} gets an email with the link, open until {toDate(q?.validUntil)}.
                    From now on the offer is frozen: changing it means making a new version.
                </p>
            </Modal>

            <Modal id="modal-enterprise-approve" title="Approve this offer" buttonText="Approve" buttonColor="warning"
                onConfirm={cb => act("approve", { note: approvalNote }, "Approved.", undefined, cb)}>
                <p>
                    {q?.discountPercent}% discount and a {q?.marginPercent}% margin need {q?.requiredApproval === "director" ? "a director's" : "a sales manager's"} approval.
                    You approve as a director.
                </p>
                <textarea className="form-control form-control-solid" rows={2} placeholder="Why (optional)"
                    value={approvalNote} onChange={e => approvalNoteSet(e.target.value)} />
            </Modal>

            <Modal id="modal-enterprise-version" title="Make a new version?" buttonText="Make new version" buttonColor="primary"
                onConfirm={cb => act("version", {}, "New version created as a draft.", nq => props.onOpenVersion(nq.id!), cb)}>
                <p className="mb-0">
                    A copy of this offer is made as a draft for you to change and send. <b>This version's link stops working now</b>,
                    so the customer cannot accept figures that are no longer on offer.
                </p>
            </Modal>

            <Modal id="modal-enterprise-revoke" title="Withdraw this offer?" buttonText="Withdraw" buttonColor="danger"
                onConfirm={cb => act("revoke", { reason: revokeReason }, "Offer withdrawn.", undefined, cb)}>
                <p>The link stops working at once. This cannot be undone; make a new version to offer again.</p>
                <input className="form-control form-control-solid" placeholder="Reason, shown in the history (optional)"
                    value={revokeReason} onChange={e => revokeReasonSet(e.target.value)} />
            </Modal>

            <Modal id="modal-enterprise-delete" title="Delete this draft?" buttonText="Delete" buttonColor="danger"
                onConfirm={cb => {
                    hub.Delete("/system/enterprise/" + props.id, res => {
                        cb(!res.success)
                        if (!res.success) return toastError(res.message ?? "Couldn't delete it.")
                        toastSuccess("Draft deleted.")
                        props.onChanged()
                        drawer.current?.close()
                    })
                }}>
                <p className="mb-0">It was never sent, so nobody else has seen it. It is removed with its history.</p>
            </Modal>
        </Drawer>
    )
}


const rank = (level?: string) => level === "director" ? 2 : level === "manager" ? 1 : 0


function Step(props: { done: boolean, warn?: boolean, title: string, text: string, children?: React.ReactNode }) {
    return (
        <div className={"ent-step" + (props.done ? " is-done" : "") + (props.warn ? " is-warn" : "")}>
            <div className="ent-step-mark"><Icon icon={props.done ? "check" : props.warn ? "xmark" : "circle"} type="solid" size={9} /></div>
            <div className="min-w-0 flex-grow-1">
                <div className="fw-semibold text-gray-900 fs-7">{props.title}</div>
                <div className="text-gray-600 fs-8">{props.text}</div>
                {props.children}
            </div>
        </div>
    )
}

function Fact(props: { label: string, value: string }) {
    return (
        <div className="ent-fact">
            <span>{props.label}</span>
            <b>{props.value}</b>
        </div>
    )
}
